import { Fragment, FC, useEffect, useState } from "react";
import { Box, Tooltip, Typography } from "@mui/material";
import { RedoxResultType } from "DataTypes/Services/Patient.type";
import Loader from "Components/Loader";
import NoData from "Components/NoData";
import { SideModalProps } from "DataTypes/Modal";
import { PatientInitialStateType } from "DataTypes/Redux";
import { useSelector } from "react-redux";
import RedoxFormatter from "Utils/Redox.Formatter";
import DateTime from "Utils/DateTime";
import UpdateResult from "./UpdateResult";
import { CommonStyle } from "Components";
import ImgPath from "Constant/Imgs";
import RedoxApi from "Service/Redox.api";
import CstmBtn from "Components/CstmBtn";
import useEhrSync from "Hooks/useEhrSync";
import EhrLastSync from "Components/EhrLastSync";
import useEhrRefresh from "Hooks/useEhrRefresh";
import { EhrDomains } from "Constant/Ehr";
import { label } from "Constant/En";

export interface DataActionType {
  data: RedoxResultType | null;
  action: "ADD" | "EDIT" | "VIEW" | "DELETE" | null;
}

const Result: FC<SideModalProps> = (props) => {
  const { onClose } = props;
  const { patientInfo, patientOrg }: PatientInitialStateType = useSelector(
    (state: any) => state?.patient,
  );
  const [resultData, setResultData] = useState<RedoxResultType[]>([]);
  const [loading, setLoading] = useState(true);
  const [dataAction, setDataAction] = useState<DataActionType>({
    data: null,
    action: null,
  });

  useEffect(() => {
    fetchResults();
  }, []);

  const fetchResults: () => void = async () => {
    setLoading(true);
    const res = await RedoxApi.ResultSearch({ id1: patientInfo.id });
    if (res?.success) {
      setResultData(RedoxFormatter.ResultList(res?.data?.results));
    } else setResultData([]);
    setLoading(false);
  };

  const ehrSync = useEhrSync(EhrDomains.LAB_RESULTS);
  useEhrRefresh(EhrDomains.LAB_RESULTS, fetchResults);

  const handleCloseLayer2: () => void = () => {
    setDataAction({ data: null, action: null });
  };

  const displayResultHead: (data: RedoxResultType) => React.ReactElement = (
    data,
  ) => {
    return (
      <Box sx={{ ...sxMedBox }}>
        <Typography variant="subtitle1" sx={{ ...sxMedName }}>
          {data.resultName} — {data.observationName}
        </Typography>
      </Box>
    );
  };

  const Layer1Header = (
    <Box sx={{ ...sxHeader, ...CommonStyle.sxRow }}>
      <Typography fontWeight="bold">Results</Typography>
      {/* Controls on one line, with the sync stamp beneath them:
          stacking it under the button alone makes that column taller
          and drops the icons out of line with it. */}
      <Box
        sx={{
          ...CommonStyle.sxFlexDirectionColumn,
          alignItems: "flex-end",
        }}
      >
        <Box sx={{ ...CommonStyle.sxRow }}>
          {ehrSync.canSync && (
            <Tooltip arrow placement="top" title={ehrSync.title}>
              <span>
                <CstmBtn
                  label={label.syncFromEhr}
                  btnType="primary"
                  loading={ehrSync.syncing}
                  loadingText="Syncing..."
                  onClick={ehrSync.handleSync}
                  sxProps={{ mr: 1, py: 0.4, minWidth: "auto", px: 1.2 }}
                />
              </span>
            </Tooltip>
          )}
          <Box
            sx={{ ...CommonStyle.sxRoundIconsMainSm }}
            onClick={() => {
              handleCloseLayer2();
              onClose();
            }}
          >
            <ImgPath.CloseIcon titleAccess="Close" />
          </Box>
        </Box>
        <EhrLastSync sync={ehrSync} />
      </Box>
    </Box>
  );

  const Layer1 = (
    <Fragment>
      {Layer1Header}
      <Box sx={{ ...sxMain }}>
        {loading ? (
          <Loader />
        ) : resultData.length ? (
          resultData.map((el) => {
            return (
              <Box key={el.id} sx={{ ...sxContainer }}>
                {displayResultHead(el)}
                <Typography variant="subtitle1">
                  {el.observationValue
                    ? [el.observationValue, el.observationUnit]
                        .filter(Boolean)
                        .join(" ")
                    : "—"}
                </Typography>
                <Typography variant="subtitle1">
                  Observation Date: {DateTime.ToLocalDate(el.dateTime)}
                </Typography>
                <Typography variant="subtitle1">
                  Status: {el.status || "—"}
                </Typography>
              </Box>
            );
          })
        ) : (
          <NoData />
        )}
      </Box>
    </Fragment>
  );

  return (
    <Fragment>
      {/* The list column keeps a pixel floor so its rows stop collapsing
          into a horizontal scroll on a narrow screen, and the panel beside
          it takes whatever width is left. */}
      <Box
        sx={{
          ...CommonStyle.sxFlexDirectionColumn,
          width: "30%",
          minWidth: 320,
          flexShrink: 0,
          overflow: "hidden",
        }}
      >
        {Layer1}
      </Box>
      <Box width="1%"></Box>
      {dataAction.action && (
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <UpdateResult
            dataAction={dataAction}
            onClose={handleCloseLayer2}
            refreshList={fetchResults}
          />
        </Box>
      )}
    </Fragment>
  );
};

export default Result;

// The header sizes to its own content rather than to a percentage of the
// modal: with the sync stamp on a second line it needs more than the 10% a
// short viewport gives it, and the overflow used to land on the list below.
const sxHeader = {
  justifyContent: "space-between",
  flexShrink: 0,
  py: 1,
  px: 2,
};

const sxMain = {
  flex: 1,
  minHeight: 0,
  overflow: "auto",
  px: 2,
};

const sxContainer = {
  py: 1,
  borderBottom: "1px solid",
  borderBottomColor: "custom.main",
};

const sxMedBox = {
  display: "flex",
  flexDirection: "row",
  justifyContent: "space-between",
};

const sxMedName = {
  fontWeight: "bold",
  width: "80%",
  lineBreak: "anywhere",
};
